import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";
import type { Id } from "../../../convex/_generated/dataModel";
import {
  UsersIcon,
  OnlineIcon,
  ChatIcon,
  CrownIcon,
  ShieldIcon,
  BanIcon,
  ReplyIcon,
  TrashIcon,
} from "@/components/icons";

export interface OrganizationMessage {
  _id: Id<"messages">;
  content: string;
  type: string;
  createdAt: number;
  editedAt?: number;
  replyTo?: {
    messageId: Id<"messages">;
    author: { id: Id<"users">; username: string } | null;
    content: string;
  } | null;
  author: { id: Id<"users">; username: string; avatarUrl?: string } | null;
}

export interface OrganizationMember {
  _id: Id<"members">;
  role: string;
  user: {
    id: Id<"users">;
    username?: string;
    avatarUrl?: string;
    isOnline: boolean;
    isInGameOnline: boolean;
  } | null;
}

export function MessageBubble({
  message,
  currentUserId,
  canDelete = false,
  onDelete,
  onReply,
}: {
  message: OrganizationMessage;
  currentUserId?: Id<"users">;
  canDelete?: boolean;
  onDelete?: (messageId: Id<"messages">) => void;
  onReply?: (message: OrganizationMessage) => void;
}) {
  const isSystem = message.type === "system";
  const highlightForUser =
    Boolean(currentUserId) && message.replyTo?.author?.id === currentUserId;

  if (isSystem) {
    return (
      <div className="text-center text-sm text-muted">
        {message.content}
      </div>
    );
  }

  return (
    <div
      className={cn(
        "group flex gap-3 rounded-lg p-2 -m-2",
        highlightForUser && "bg-primary/5 ring-1 ring-primary/20"
      )}
    >
      <Avatar className="h-8 w-8 flex-shrink-0">
        <AvatarImage src={message.author?.avatarUrl} />
        <AvatarFallback>
          {message.author?.username?.charAt(0) || "?"}
        </AvatarFallback>
      </Avatar>
      <div className="flex-1">
        <div className="flex items-baseline gap-2">
          <span className="font-medium text-foreground">
            {message.author?.username || "anonymous"}
          </span>
          <span className="text-xs text-muted">
            {formatTime(message.createdAt)}
          </span>
          {message.editedAt && (
            <span className="text-xs text-muted">(edited)</span>
          )}
          <div className="ml-auto flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
            {onReply && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-xs"
                onClick={() => onReply(message)}
              >
                <ReplyIcon className="h-3.5 w-3.5" />
                Reply
              </Button>
            )}
            {canDelete && onDelete && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-xs text-destructive hover:text-destructive hover:bg-destructive/10"
                onClick={() => onDelete(message._id)}
              >
                <TrashIcon className="h-3.5 w-3.5" />
                Delete
              </Button>
            )}
          </div>
        </div>
        {message.replyTo && (
          <div className="mt-1 rounded-md border border-border bg-foreground/5 px-2 py-1 text-xs text-muted">
            Replying to{" "}
            <span className="font-medium text-foreground">
              {message.replyTo.author?.username || "anonymous"}
            </span>
            :{" "}
            <span className="inline-block max-w-full align-bottom truncate">
              {message.replyTo.content}
            </span>
          </div>
        )}
        <p className="mt-1 text-foreground">{message.content}</p>
      </div>
    </div>
  );
}

export interface MemberManageActions {
  actorRole: "owner" | "admin" | "moderator" | "member";
  onChangeRole?: (memberId: Id<"members">, role: "admin" | "moderator" | "member") => void;
  onKick?: (memberId: Id<"members">, username: string) => void;
  onBan?: (memberId: Id<"members">, username: string) => void;
}

const roleHierarchy: Record<string, number> = {
  owner: 4,
  admin: 3,
  moderator: 2,
  member: 1,
};

function canManageMember(actorRole: string, targetRole: string) {
  return roleHierarchy[actorRole] > roleHierarchy[targetRole];
}

export function MemberRow({
  member,
  manage,
}: {
  member: OrganizationMember;
  manage?: MemberManageActions;
}) {
  const [showActions, setShowActions] = useState(false);
  const RoleIcon =
    member.role === "owner"
      ? CrownIcon
      : ["admin", "moderator"].includes(member.role)
        ? ShieldIcon
        : null;

  const canManage =
    manage &&
    member.role !== "owner" &&
    canManageMember(manage.actorRole, member.role);

  // Determine which roles the actor can assign (must be strictly below their own)
  const assignableRoles: ("admin" | "moderator" | "member")[] = [];
  if (canManage) {
    if (roleHierarchy[manage.actorRole] > roleHierarchy["admin"])
      assignableRoles.push("admin");
    if (roleHierarchy[manage.actorRole] > roleHierarchy["moderator"])
      assignableRoles.push("moderator");
    assignableRoles.push("member");
  }

  // Only ban is admin/owner (hierarchy >= 3)
  const canBan = canManage && roleHierarchy[manage.actorRole] >= 3;

  return (
    <div className="group flex items-center gap-3 p-3">
      <div className="relative">
        <Avatar className="h-8 w-8">
          <AvatarImage src={member.user?.avatarUrl} />
          <AvatarFallback>
            {member.user?.username?.charAt(0) || "?"}
          </AvatarFallback>
        </Avatar>
        {member.user?.isInGameOnline && (
          <span className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-background bg-primary" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className="truncate text-sm font-medium text-foreground">
          {member.user?.username || "anonymous"}
        </p>
        {showActions && canManage && (
          <div className="mt-2 flex flex-col gap-2">
            {/* Role select */}
            {manage.onChangeRole && assignableRoles.length > 0 && (
              <select
                className="w-full rounded-md border border-border bg-background px-2 py-1 text-xs text-foreground"
                value={member.role}
                onChange={(e) => {
                  const newRole = e.target.value as "admin" | "moderator" | "member";
                  if (newRole !== member.role) {
                    manage.onChangeRole!(member._id, newRole);
                  }
                }}
              >
                {assignableRoles.map((r) => (
                  <option key={r} value={r}>
                    {r.charAt(0).toUpperCase() + r.slice(1)}
                  </option>
                ))}
              </select>
            )}
            <div className="flex gap-1">
              {manage.onKick && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-6 px-2 text-xs"
                  onClick={() =>
                    manage.onKick!(
                      member._id,
                      member.user?.username || "this member"
                    )
                  }
                >
                  Kick
                </Button>
              )}
              {canBan && manage.onBan && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-6 px-2 text-xs text-destructive hover:text-destructive hover:bg-destructive/10"
                  onClick={() =>
                    manage.onBan!(
                      member._id,
                      member.user?.username || "this member"
                    )
                  }
                >
                  <BanIcon className="mr-1 h-3 w-3" />
                  Ban
                </Button>
              )}
            </div>
          </div>
        )}
      </div>
      <div className="flex items-center gap-1">
        {RoleIcon && (
          <RoleIcon
            className={`h-4 w-4 ${member.role === "owner" ? "text-yellow-500" : "text-muted"}`}
          />
        )}
        {canManage && (
          <button
            type="button"
            className="ml-1 rounded p-0.5 text-muted opacity-0 transition-opacity hover:text-foreground group-hover:opacity-100"
            onClick={() => setShowActions((v) => !v)}
            aria-label="Manage member"
          >
            <svg viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4">
              <circle cx="12" cy="5" r="2" />
              <circle cx="12" cy="12" r="2" />
              <circle cx="12" cy="19" r="2" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
}

// Row for displaying a banned member with unban action
export function BannedMemberRow({
  member,
  onUnban,
}: {
  member: {
    _id: Id<"members">;
    banReason?: string;
    user: { id: Id<"users">; username: string; avatarUrl?: string } | null;
  };
  onUnban: (memberId: Id<"members">) => void;
}) {
  return (
    <div className="flex items-center gap-3 p-3">
      <Avatar className="h-8 w-8">
        <AvatarImage src={member.user?.avatarUrl} />
        <AvatarFallback>
          {member.user?.username?.charAt(0) || "?"}
        </AvatarFallback>
      </Avatar>
      <div className="flex-1 min-w-0">
        <p className="truncate text-sm font-medium text-foreground">
          {member.user?.username || "anonymous"}
        </p>
        {member.banReason && (
          <p className="truncate text-xs text-muted">
            Reason: {member.banReason}
          </p>
        )}
      </div>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="h-7 px-2 text-xs"
        onClick={() => onUnban(member._id)}
      >
        Unban
      </Button>
    </div>
  );
}

export function buildOrganizationStats({
  memberCount,
  onlineCount,
}: {
  memberCount: number;
  onlineCount: number;
}) {
  return [
    {
      label: `${memberCount} members`,
      icon: <UsersIcon className="h-4 w-4" />,
      className: "text-muted",
    },
    {
      label: `${onlineCount} online`,
      icon: <OnlineIcon className="h-4 w-4" />,
      className: "text-primary",
    },
  ];
}

export const organizationIcons = {
  chat: <ChatIcon className="h-5 w-5" />,
  members: <UsersIcon className="h-5 w-5" />,
};

function formatTime(timestamp: number) {
  const date = new Date(timestamp);
  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();

  if (isToday) {
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  }

  return date.toLocaleDateString([], {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}
